import { useEffect, useMemo, useRef, useState } from "react";
import CalendarContainer from "@/components/tpr/CalendarContainer/CalendarContainer";
import { Button } from "@/components/ui/button";
import { BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { isElevatedUser } from "@/utils/userPermissions";
import { ChevronLeft, Lock } from "lucide-react";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useMember } from "@/contexts/memberContext";
import { useCalendarData } from "@/hooks/useCalendarData";
import { CalendarProvider } from "@/providers/CalendarProvider";
import { distinctRoomAmenityNames } from "@/utils/roomAmenityFilter";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { Location } from "@/types/Location";
import type { Room } from "@/types/Room";
import type { Amenity } from "@/types/Amenity";
import { tprStorage } from "@/core/storage";
import { localDayToUtcMidnight, utcDayKey } from "@/lib/calendarDay";
import { resolveAmenitySelection, type StoredAmenityPrefs } from "@/lib/mergeAmenitySelection";
import { sortRoomsByDisplayOrder } from "@/lib/utils";
import { AmenityService } from "@/services/AmenityService";

const LocationRoomSchedule = () => {
  const today = new Date();
  const { member } = useMember();

  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setBreadcrumb } = useBreadcrumb();

  // Parse date from URL parameter or use today
  const getInitialDate = (): Date => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      // The URL param is a "yyyy-MM-dd" utcDayKey, so `new Date(...)` already yields UTC-midnight.
      const parsedDate = new Date(dateParam);
      // Validate the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    // `today` is a LOCAL-time instant; anchor it to UTC-midnight of the local calendar day so the
    // UTC-anchored grid/header/fetch open on the day the viewer calls "today" rather than the UTC
    // day of "now" (which is the previous day for a viewer ahead of UTC early in the morning).
    return localDayToUtcMidnight(today);
  };

  // Local UI state
  const [date, setDate] = useState<Date | null>(getInitialDate());
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [calendarType, setCalendarType] = useState<"day" | "week">(
    member?.settings?.calendarTypePreference || "day",
  );

  // Filter state
  const [selectedRooms, setSelectedRooms] = useState<Room[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<Amenity[]>([]);
  const [availableAmenities, setAvailableAmenities] = useState<Amenity[]>([]);
  // Room-amenity filter: the names the user has ticked, plus the options derived from the rooms.
  const [selectedRoomAmenities, setSelectedRoomAmenities] = useState<string[]>([]);

  // Refs to track loading state and last loaded slug
  const isLoadingRoomsRef = useRef(false);
  const lastLoadedSlugRef = useRef<string>("");
  const lastLoadedSlugForAmenitiesRef = useRef<string>("");

  const slug = params.locationSlug || "";
  if (!slug) navigate("/dashboard");

  // React Query hook replaces all data fetching and loading state management
  const {
    availableLocations,
    availableRooms,
    bookings,
    isLoading,
    isLocationLoading,
    isRoomLoading,
    isDateLoading,
    refetchBookings,
  } = useCalendarData({
    locationSlug: slug,
    date,
    calendarType,
  });

  // Set the selected location based on the URL slug
  useEffect(() => {
    if (availableLocations && availableLocations.length > 0 && slug) {
      const locationFromSlug = availableLocations.find((loc) => loc.slug === slug);
      if (locationFromSlug && locationFromSlug.id !== selectedLocation?.id) {
        setSelectedLocation(locationFromSlug);
      }
    }
  }, [availableLocations, slug, selectedLocation, setSelectedLocation]);

  // Update calendar type when member settings change
  useEffect(() => {
    if (member?.settings?.calendarTypePreference) {
      setCalendarType(member.settings.calendarTypePreference);
    }
  }, [member?.settings?.calendarTypePreference]);

  useEffect(() => {
    if (availableRooms && availableRooms.length > 0) {
      setSelectedRoom(availableRooms[0]);
    } else {
      setSelectedRoom(null);
    }
  }, [availableRooms]);

  // Room-amenity filter options: the distinct amenity names present across this location's rooms.
  const availableRoomAmenities = useMemo(() => distinctRoomAmenityNames(availableRooms ?? []), [availableRooms]);

  // Reset the room-amenity filter when the location changes — a name selected at one location may
  // not exist at another and would otherwise hide every room.
  useEffect(() => {
    setSelectedRoomAmenities([]);
  }, [slug]);

  // Update URL when date changes. `date` may be a UTC-midnight instant (parsed from a previous
  // "date" param, or set from a UTC-anchored grid cell) — date-fns' `format` reads the browser's
  // LOCAL calendar day, which under a non-UTC, negative-offset viewer TZ (e.g. America/New_York)
  // reads a UTC-midnight instant as the PREVIOUS day. Writing that back to the URL would drift the
  // date by a day on every round-trip (e.g. reload, or getInitialDate re-parsing it). `utcDayKey`
  // keeps the URL's day in sync with the UTC day the grid actually renders.
  useEffect(() => {
    if (date) {
      const dateStr = utcDayKey(date);
      const currentDateParam = searchParams.get("date");

      // Only update if the date has changed to avoid unnecessary navigation
      if (currentDateParam !== dateStr) {
        setSearchParams({ date: dateStr }, { replace: true });
      }
    }
  }, [date, searchParams, setSearchParams]);

  // Load selected rooms from localStorage for current location and calendar type
  useEffect(() => {
    if (!availableRooms || !slug) return;

    const slugChanged = lastLoadedSlugRef.current !== slug;

    // Only skip if we're on same location AND have rooms AND not currently loading
    if (!slugChanged && selectedRooms.length > 0 && !isLoadingRoomsRef.current) {
      // Re-sort selectedRooms using fresh room data from availableRooms (for updated displayOrder)
      setSelectedRooms((prev) => {
        const selectedIds = new Set(prev.map((r) => r.id));
        const freshRooms = availableRooms.filter((r) => selectedIds.has(r.id));
        return sortRoomsByDisplayOrder(freshRooms);
      });
      return;
    }

    // Set loading flag to prevent save effect from running with stale data
    isLoadingRoomsRef.current = true;

    // Dynamic key based on calendar type
    const roomsKey = `brick${calendarType === "day" ? "Day" : "Week"}Schedule.selectedRoomsByLocation`;
    const allSelections = tprStorage.get<Record<string, string[]>>(roomsKey) || {};
    const savedIds = allSelections[slug] || [];

    if (Array.isArray(savedIds) && savedIds.length > 0) {
      // Filter to keep only saved rooms, then sort by floor order
      const validRooms = availableRooms.filter((room) => savedIds.includes(room.id));
      setSelectedRooms(sortRoomsByDisplayOrder(validRooms));
    } else {
      // Default to all rooms for this location, sorted by floor
      setSelectedRooms(sortRoomsByDisplayOrder(availableRooms));
    }

    // Update ref and clear loading flag
    lastLoadedSlugRef.current = slug;
    isLoadingRoomsRef.current = false;
  }, [availableRooms, slug, calendarType]);

  // Save selected room ids for current location to localStorage whenever selection changes
  useEffect(() => {
    // Don't save until we've loaded from localStorage for this location
    if (!slug || isLoadingRoomsRef.current || lastLoadedSlugRef.current !== slug) return;

    // Don't save if selectedRooms contains rooms from a different location
    if (selectedRooms.length > 0 && availableRooms && availableRooms.length > 0) {
      const availableRoomIds = new Set(availableRooms.map((r) => r.id));
      const allRoomsValid = selectedRooms.every((room) => availableRoomIds.has(room.id));
      if (!allRoomsValid) return;
    }

    const roomsKey = `brick${calendarType === "day" ? "Day" : "Week"}Schedule.selectedRoomsByLocation`;
    const allSelections = tprStorage.get<Record<string, string[]>>(roomsKey) || {};
    allSelections[slug] = selectedRooms.map((room) => room.id);
    tprStorage.set(roomsKey, allSelections);
  }, [selectedRooms, slug, availableRooms, calendarType]);

  // Load bookable amenities for current location
  useEffect(() => {
    if (!slug || !selectedLocation) return;

    // Skip if already loaded for this location; a remount or location change re-fetches.
    if (lastLoadedSlugForAmenitiesRef.current === slug && availableAmenities.length > 0) return;

    const amenityService = new AmenityService();
    amenityService.getBookableAmenitiesForLocation(selectedLocation.id).then((amenities) => {
      setAvailableAmenities(amenities);

      // Load saved selection for this location
      const amenitiesKey = `brick${calendarType === "day" ? "Day" : "Week"}Schedule.selectedAmenitiesByLocation`;
      const allSelections = tprStorage.get<Record<string, StoredAmenityPrefs | string[]>>(amenitiesKey) || {};
      const { selectedIds } = resolveAmenitySelection(amenities.map((a) => a.id), allSelections[slug]);
      setSelectedAmenities(amenities.filter((a) => selectedIds.includes(a.id)));

      lastLoadedSlugForAmenitiesRef.current = slug;
    });
  }, [slug, selectedLocation, calendarType]);

  // Save selected amenity ids for current location to localStorage whenever selection changes
  useEffect(() => {
    // Don't save until we've loaded from localStorage for this location
    if (!slug || lastLoadedSlugForAmenitiesRef.current !== slug) return;
    const amenitiesKey = `brick${calendarType === "day" ? "Day" : "Week"}Schedule.selectedAmenitiesByLocation`;
    const allSelections = tprStorage.get<Record<string, StoredAmenityPrefs | string[]>>(amenitiesKey) || {};
    allSelections[slug] = {
      selected: selectedAmenities.map((a) => a.id),
      known: availableAmenities.map((a) => a.id),
    };
    tprStorage.set(amenitiesKey, allSelections);
  }, [selectedAmenities, slug, calendarType, availableAmenities]);

  useEffect(() => {
    setBreadcrumb(
      <>
        <BreadcrumbEllipsis className="md:hidden" />
        {/* <BreadcrumbItem className="hidden md:flex text-sm text-gray-600">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5">
              Locations <ChevronDownIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {availableLocations?.map((l) => (
                <DropdownMenuItem key={l.slug}>
                  <BreadcrumbLink href={`/location/${l.slug}`}>{l.name}</BreadcrumbLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
        <BreadcrumbSeparator /> */}
        <BreadcrumbItem className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[125px] md:max-w-none">
          <BreadcrumbLink href="#">
            <span className="hidden md:inline">{selectedLocation?.city}</span> {selectedLocation?.name || "Location"}{" "}
            Calendar
          </BreadcrumbLink>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [setBreadcrumb, availableLocations, selectedLocation]);

  // Member-facing calendar gates (elevated users bypass entirely and always see the calendar):
  //  • no-access — the slug isn't in the member's active/permissioned list (inactive or not permitted);
  //  • booking-blocked — the location is active + permissioned but bookings are blocked during setup.
  const isElevated = isElevatedUser(member);
  const locationsReady = !isLoading && !isLocationLoading && availableLocations != null;
  const locFromSlug = availableLocations?.find((l) => l.slug === slug) ?? null;
  const noAccess = !isElevated && locationsReady && !!slug && !locFromSlug;
  const bookingBlocked = !isElevated && !!locFromSlug && locFromSlug.bookingLocked === true;

  if (noAccess || bookingBlocked) {
    return (
      <div className="min-h-screen p-4" data-testid="calendar-member-gate">
        <Button variant="ghost" className="mb-6" onClick={() => navigate("/dashboard")}>
          <ChevronLeft /> Back to dashboard
        </Button>
        <div className="max-w-xl mx-auto mt-12 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-8 text-center">
          <Lock className="w-8 h-8 mx-auto text-gray-400 mb-4" />
          {noAccess ? (
            <p className="text-gray-700 dark:text-gray-200" data-testid="calendar-no-access">
              This centre isn't available to you right now. If you think this is an error, please contact Head Office.
            </p>
          ) : (
            <p className="text-gray-700 dark:text-gray-200" data-testid="calendar-booking-blocked">
              You have access to this centre, but we're currently not allowing bookings at the moment. Please check back
              later. If you feel this is an error, please contact Head Office.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-slate-900 w-full border-b border-b-gray-200 dark:border-b-gray-700">
        <CalendarProvider
          slug={slug}
          member={member}
          date={date}
          setDate={setDate}
          calendarType={calendarType}
          setCalendarType={setCalendarType}
          availableLocations={availableLocations}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          availableRooms={availableRooms}
          selectedRoom={selectedRoom}
          setSelectedRoom={setSelectedRoom}
          selectedRooms={selectedRooms}
          setSelectedRooms={setSelectedRooms}
          selectedAmenities={selectedAmenities}
          setSelectedAmenities={setSelectedAmenities}
          availableAmenities={availableAmenities}
          selectedRoomAmenities={selectedRoomAmenities}
          setSelectedRoomAmenities={setSelectedRoomAmenities}
          availableRoomAmenities={availableRoomAmenities}
          isLoading={isLoading}
          isDateLoading={isDateLoading}
          isLocationLoading={isLocationLoading}
          isRoomLoading={isRoomLoading}
          bookings={bookings}
          refreshBookings={refetchBookings}
        >
          <CalendarContainer />
        </CalendarProvider>
      </div>
    </>
  );
};

export default LocationRoomSchedule;
